/**
 * DynamoDB record types for the Submissions table.
 *
 * Single-table design:
 *   PK = SUB#{submission_id}
 *   SK = META
 *
 * GSI1 (TypeDateIndex):  submission_type (PK) + submitted_at (SK)
 * GSI2 (StatusDateIndex): status (PK) + submitted_at (SK)
 */

// ─── Status enum ───────────────────────────────────────────────────────────
export type SubmissionStatus =
  | 'new'
  | 'in_review'
  | 'approved'
  | 'rejected'
  | 'funded'
  | 'synced'
  | 'sync_failed';

export type SubmissionType = 'form' | 'api';

export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

// ─── Lead source (company the lead belongs to) ─────────────────────────────
// Absent on records created before the New Western form existed — those are
// all Left Main leads, which is why the accessor below defaults to leftmain.
export type LeadSource = 'leftmain' | 'newwestern';

/** Single source of truth for the "absent lead_source means Left Main" rule. */
export function getLeadSource(record: { lead_source?: LeadSource } | null | undefined): LeadSource {
  return record?.lead_source ?? 'leftmain';
}

// ─── Audit trail ───────────────────────────────────────────────────────────
export type AuditAction =
  | 'created'
  | 'status_changed'
  | 'fields_updated'
  | 'sync_requested'
  | 'sync_completed'
  | 'sync_failed'
  | 'manual_resync'
  | 'note_added'
  | 'email_sent'
  | 'email_failed'
  | 'pof_uploaded'
  | 'pof_deleted';

export interface AuditEntry {
  action: AuditAction;
  timestamp: string;     // ISO 8601
  user?: string;         // email of actor (system if automated)
  details?: string;      // human-readable description
  metadata?: Record<string, unknown>; // structured data for the event
}

// ─── Proof of Funds letter (New Western approved applications) ─────────────
// Uploaded by portal staff and attached to the approved decision email.
// Stored in the intake bucket under nw-pof/ (NOT uploads/, which auto-expires).
export interface PofLetterMeta {
  s3_key: string;
  file_name: string;
  size_bytes: number;
  uploaded_at: string;   // ISO 8601
  uploaded_by: string;   // portal user email
}

// ─── Notes ─────────────────────────────────────────────────────────────────
export interface NoteEntry {
  id: string;            // unique note ID (ulid)
  text: string;          // note content
  author: string;        // email of note author
  created_at: string;    // ISO 8601
}

// ─── Base fields (shared by both form and API submissions) ─────────────────
export interface SubmissionRecordBase {
  // DynamoDB keys
  PK: string;            // SUB#{submission_id}
  SK: 'META';

  // Core identifiers
  submission_id: string;
  submission_type: SubmissionType;
  submission_source: string;
  submitted_at: string;  // ISO 8601

  // Company the lead belongs to. Absent = Left Main (legacy records predate
  // the field) — always read via getLeadSource(), never directly.
  lead_source?: LeadSource;

  // Workflow
  status: SubmissionStatus;
  ready_to_sync: boolean;
  synced_at?: string;
  sync_status?: SyncStatus;
  sync_error?: string;

  // SLA tracking
  reviewed_at?: string;  // ISO 8601 — timestamp of first approve/reject decision

  // Internal tracking (not sent to partners)
  projected_close_date?: string; // ISO 8601 date (YYYY-MM-DD)

  // Audit
  updated_by?: string;   // email of last editor
  updated_at?: string;   // ISO 8601
  created_at: string;    // ISO 8601
  audit_log?: AuditEntry[];

  // Notes
  notes?: NoteEntry[];

  // Funding (API disbursements only)
  funded_at?: string;    // ISO 8601 — when marked as funded
  funding_sync_status?: SyncStatus;
  funding_synced_at?: string;
}

// ─── Form Submission Record ────────────────────────────────────────────────
export interface FormSubmissionRecord extends SubmissionRecordBase {
  submission_type: 'form';

  // Partner tracking (pre-filled via URL when LeftMain forwards users).
  // Absent on New Western public-form submissions, which have no partner.
  partner_submission_id?: string;

  // ── New Western submitter-email tracking (lead_source === 'newwestern') ──
  // Extra recipients CC'd on the decision email, managed in the portal.
  notification_emails?: string[];
  // At-most-once send tracking per email kind: a claim is written with a
  // conditional update BEFORE the SES send; sent_at confirms delivery handoff;
  // error records a failed send (claim intentionally kept — no auto-retry).
  thank_you_email_claimed_at?: string;
  thank_you_email_sent_at?: string;
  thank_you_email_error?: string;
  decision_email_claimed_at?: string;
  decision_email_sent_at?: string;
  decision_email_error?: string;
  // One-time corrected-decision email after an explicit "amend decision".
  decision_email_correction_sent_at?: string;
  // Draw-processed notification (approved NW submissions only, sent after
  // the decision email as the explicit third portal step).
  draw_processed_email_claimed_at?: string;
  draw_processed_email_sent_at?: string;
  draw_processed_email_error?: string;
  // Internal "Sync to Accounting" (Step 4) — ONE sync per form, same
  // at-most-once claim semantics as the submitter emails above.
  accounting_email_claimed_at?: string;
  accounting_email_sent_at?: string;
  accounting_email_error?: string;
  // Proof of Funds letter attached to the approved decision email.
  // Required before an approval email can be sent.
  pof_letter?: PofLetterMeta;

  // ── New Western disbursement (Step 4 — manual, NW never syncs) ──────────
  // Wire and fee details recorded by staff once the draw has been disbursed.
  // Saving a complete wire-in marks the record 'funded' and notifies the
  // accounting mailbox (nwAccountingRecipients). The FS (front-side) title
  // company is NOT duplicated here — it is the existing pref_title_co_name
  // field, which the portal relabels "FS Title Company" on NW records.
  wire_out_date?: string;      // YYYY-MM-DD
  wire_out_amount?: number;
  wire_in_date?: string;       // YYYY-MM-DD
  wire_in_amount?: number;
  bs_title_company?: string;   // back-side title company
  fee_amount?: number;

  // Applicant
  full_name: string;
  entity_name: string;
  dba?: string;

  // Contact
  email: string;
  phone: string;

  // Mailing Address
  mailing_address_1: string;
  mailing_address_2?: string;
  mailing_city: string;
  mailing_state: string;
  mailing_zip: string;

  // Credit Request
  loc_amount_requested: number;
  markets_operating: string;
  avg_annual_transactions: number;
  avg_purchase_price: number;

  // Preferences (from form)
  preferred_title_company?: string;

  // Preferred Title Company (filled by rep in portal)
  pref_title_co_name?: string;
  pref_title_co_email?: string;
  pref_title_co_phone?: string;
}

// ─── API Submission Record ─────────────────────────────────────────────────
export interface ApiSubmissionRecord extends SubmissionRecordBase {
  submission_type: 'api';

  // Partner context
  partner_name?: string;
  partner_submission_id: string;

  // Property
  property_address_1: string;
  property_address_2?: string;
  property_city: string;
  property_state: string;
  property_zip: string;

  // Funding
  contract_purchase_price: number;
  fund_amount_requested: number;
  estimated_closing_date: string;

  // Front Side Title Company
  front_title_company_name: string;
  front_title_contact_name: string;
  front_title_contact_email: string;
  front_title_contact_phone?: string;

  // Back Side Title Company
  back_title_company_name: string;
  back_title_contact_name: string;
  back_title_contact_email: string;
  back_title_contact_phone?: string;

  // Document references
  document_count?: number;
  drive_folder_url?: string;
  drive_file_urls?: string;

  // Financial tracking (filled by rep in portal)
  fund_out_amount?: number;
  fund_out_date?: string;
  fund_in_amount?: number;
  fund_in_date?: string;
}

// ─── Union type ────────────────────────────────────────────────────────────
export type SubmissionRecord = FormSubmissionRecord | ApiSubmissionRecord;

// ─── API response DTOs ─────────────────────────────────────────────────────

/** Slim DTO for list views (no full record details). */
export interface SubmissionListItem {
  submission_id: string;
  submission_type: SubmissionType;
  submitted_at: string;
  status: SubmissionStatus;
  ready_to_sync: boolean;
  synced_at?: string;
  sync_status?: SyncStatus;
  lead_source?: LeadSource;

  // Partner tracking
  partner_submission_id?: string; // both (api and form)

  // Identifiers for display
  full_name?: string;        // form
  entity_name?: string;      // form
  email?: string;            // form
  partner_name?: string;     // api
  property_address_1?: string; // api
  property_city?: string;    // api
  property_state?: string;   // api
  fund_amount_requested?: number; // both (api has it, form has loc_amount)
  loc_amount_requested?: number;  // form
  projected_close_date?: string;  // internal tracking
}

/** Paginated list response. */
export interface SubmissionListResponse {
  items: SubmissionListItem[];
  nextToken?: string;
  totalCount?: number;
}

/** Fields that portal reps can update. */
export interface SubmissionUpdateInput {
  status?: SubmissionStatus;

  // Form-specific review fields
  pref_title_co_name?: string;
  pref_title_co_email?: string;
  pref_title_co_phone?: string;

  // API-specific review fields
  front_title_contact_phone?: string;
  back_title_contact_phone?: string;
  fund_out_amount?: number;
  fund_out_date?: string;
  fund_in_amount?: number;
  fund_in_date?: string;
  funded_at?: string;

  // Sync
  ready_to_sync?: boolean;
  synced_at?: string;
  sync_status?: SyncStatus;
  sync_error?: string;
  funding_sync_status?: SyncStatus;
  funding_synced_at?: string;

  // Internal tracking
  projected_close_date?: string;

  // New Western decision-email recipients (NW records only; backend rejects
  // this field on Left Main records)
  notification_emails?: string[];

  // New Western disbursement step (NW form records only; backend rejects
  // these fields on Left Main records)
  wire_out_date?: string;
  wire_out_amount?: number;
  wire_in_date?: string;
  wire_in_amount?: number;
  bs_title_company?: string;
  fee_amount?: number;
}

/** Dashboard metrics response. */
export interface SubmissionMetrics {
  totalToday: number;
  totalThisWeek: number;
  totalThisMonth: number;
  pendingReview: number;
  approved: number;
  rejected: number;
  funded: number;
  synced: number;
  syncFailed: number;
  totalApprovedAmount: number;
  totalFundedAmount: number;
  syncedApproval: number;
  syncedFunding: number;
  formCount: number;
  apiCount: number;
  avgResponseTimeHours: number | null;

  // SLA metrics
  slaMetMinutes: number | null;     // average minutes from submitted_at to reviewed_at
  slaBreaches: number;              // count of submissions reviewed after 30 min
  oldestPendingMinutes: number | null; // how long the oldest unreviewed submission has been waiting
}
